import Immutable from 'immutable';
import zip from 'lodash/zip';
import { Doc, DocList, RawDoc } from 'records/Doc';
import { ProjectId } from 'records/Project';
import {
  BatchUploadProgress,
  DocumentUploadProgress,
} from 'records/UploadProgress';
import GQLRequest from 'utils/GQLRequest';
import * as toast from 'utils/toast';
import { v4 as uuidv4 } from 'uuid';

interface UploadResponseItem {
  documentid: string;
  signedurl: string;
  filename: string;
  filesize: number;
}

export interface BatchUploadResponse {
  processrequestid: string;
  projectid: string;
  items: UploadResponseItem[];
}

export const uploadPrototypeDocument = async (
  projectid: string,
  doc: File,
  type: string | null = null
): Promise<{ processingRequestId: string; docId: string } | undefined> => {
  try {
    const {
      beginDocumentUpload: { item, processrequestid },
    } = await new GQLRequest()
      .query(
        `
          mutation BeginDocUpload($projectid: String!, $item: UploadRequestItem!, $type: DocumentUploadType) {
            beginDocumentUpload(projectid: $projectid, item: $item, uploadType: $type) {              
              projectid
              processrequestid
              item {
                documentid
                signedurl
              }
            }
          }
          `
      )
      .variables({
        projectid,
        item: {
          filename: doc.name,
          filesize: doc.size,
          clientGeneratedID: uuidv4(),
        },
        type,
      })
      .json<{
        beginDocumentUpload: {
          item: UploadResponseItem;
          processrequestid: string;
        };
      }>();

    const resp = await fetch(item.signedurl, {
      method: 'PUT',
      body: doc,
    });

    if (resp.ok) {
      // TODO: Do something with the return value of this call.
      await new GQLRequest()
        .query(
          `
          mutation CompleteDocUpload($documentid: String!, $projectid: String!) {
            completePrototypeDocumentUpload(documentid: $documentid, projectid: $projectid)
          }
          `
        )
        .variables({ documentid: item.documentid, projectid })
        .json<{ completeDocumentUpload: boolean }>();
    }

    return {
      docId: item.documentid,
      processingRequestId: processrequestid,
    };
  } catch (e) {
    console.log('uploadDocument err:', e);
  }
  return undefined;
};

export const uploadDocument = async (
  projectid: string,
  doc: File,
  type: string | null = null
): Promise<{ processingRequestId: string; docId: string } | undefined> => {
  try {
    const {
      beginDocumentUpload: { item, processrequestid },
    } = await new GQLRequest()
      .query(
        `
          mutation BeginDocUpload($projectid: String!, $item: UploadRequestItem!, $type: DocumentUploadType) {
            beginDocumentUpload(projectid: $projectid, item: $item, uploadType: $type) {              
              projectid
              processrequestid
              item {
                documentid
                signedurl
              }
            }
          }
          `
      )
      .variables({
        projectid,
        item: {
          filename: doc.name,
          filesize: doc.size,
          clientGeneratedID: uuidv4(),
        },
        type,
      })
      .json<{
        beginDocumentUpload: {
          item: UploadResponseItem;
          processrequestid: string;
        };
      }>();

    const resp = await fetch(item.signedurl, {
      method: 'PUT',
      body: doc,
    });

    if (resp.ok) {
      // TODO: Do something with the return value of this call.
      await new GQLRequest()
        .query(
          `
          mutation CompleteDocUpload($documentid: String!, $projectid: String!) {
            completeDocumentUpload(documentid: $documentid, projectid: $projectid)
          }
          `
        )
        .variables({ documentid: item.documentid, projectid })
        .json<{ completeDocumentUpload: boolean }>();
    }

    return {
      docId: item.documentid,
      processingRequestId: processrequestid,
    };
  } catch (e) {
    console.log('uploadDocument err:', e);
  }
  return undefined;
};

interface DocumentServiceToastText {
  drawingsUploaded: {
    title: string;
    message: string;
  };
}

export const uploadDocuments = async (
  projectid: string,
  documents: File[],
  toastText: DocumentServiceToastText,
  progress: (prog: BatchUploadProgress) => void = () => {},
  checkCancellation: (documentID: string) => boolean = () => {
    return false;
  }
): Promise<void> => {
  try {
    const uploadType = documents.find(
      (d) => d.name.substr(d.name.lastIndexOf('.') + 1) === 'zip'
    )
      ? 'ZIP'
      : 'DRAWING';
    let docReqs = documents.map((d) => ({
      filename: d.name,
      filesize: d.size,
      clientGeneratedID: uuidv4(),
    }));

    // if it's a zip upload, filter out any non .zip files
    if (uploadType === 'ZIP') {
      docReqs = documents
        .filter((d) => d.name.substr(d.name.lastIndexOf('.') + 1) === 'zip')
        .map((d) => ({
          filename: d.name,
          filesize: d.size,
          clientGeneratedID: uuidv4(),
        }));
    }

    const { beginBatchDocumentUpload } = await new GQLRequest()
      .query(
        `
          mutation BeginBatchDocUpload($projectid: String!, $items: [UploadRequestItem!], $uploadType: DocumentUploadType) {
            beginBatchDocumentUpload(projectid: $projectid, items: $items, uploadType: $uploadType) {              
              projectid
              processrequestid
              items {
                documentid
                signedurl
                filename
                filesize
              }
            }
          }
          `
      )
      .variables({
        projectid,
        items: docReqs,
        uploadType,
      })
      .json<{ beginBatchDocumentUpload: BatchUploadResponse }>();

    if (beginBatchDocumentUpload.items.length !== documents.length) {
      throw Error(
        `uploadDocuments err ${beginBatchDocumentUpload.items.length} != ${documents.length}`
      );
    }

    let totalUploaded = 0;
    const uploadedMap = new Map<string, number>();
    let cancelledDocUploads = new Set<string>();

    // eslint-disable-next-line no-inner-declarations
    function generateDocProgressDocList(
      allDocuments: UploadResponseItem[],
      currentDocument: UploadResponseItem
    ): DocumentUploadProgress[] {
      return allDocuments.map((doc) => {
        const uploadProgress = {
          title: doc.filename,
          id: doc.documentid,
          totalToUpload: doc.filesize,
          totalUploaded: uploadedMap.get(doc.documentid) || 0,
        } as DocumentUploadProgress;
        if (
          currentDocument.documentid === doc.documentid &&
          uploadProgress.totalToUpload === uploadProgress.totalUploaded
        ) {
          // mark upload complete with timestamp
          uploadProgress.timestamp = Date.now();
        }
        return uploadProgress;
      });
    }

    const fileUploads = zip(documents, beginBatchDocumentUpload.items).reduce(
      (acc, [doc, item]) => {
        if (doc && item) {
          const req = async (): Promise<boolean> => {
            return new Promise((resolve, reject) => {
              const uploadReq = new XMLHttpRequest();
              uploadReq.open('PUT', item.signedurl);

              uploadReq.onload = function onRequestLoad() {
                if (this.status >= 200 && this.status < 300) {
                  uploadedMap.set(item.documentid, item.filesize);
                  const docProgresses = generateDocProgressDocList(
                    beginBatchDocumentUpload.items.filter(
                      (item) => !cancelledDocUploads.has(item.documentid)
                    ),
                    item
                  );
                  progress(
                    new BatchUploadProgress(
                      docProgresses
                        .map((d) => d.totalToUpload)
                        .reduce((a, b) => a + b, 0),
                      docProgresses
                        .map((d) => d.totalUploaded)
                        .reduce((a, b) => a + b, 0),
                      beginBatchDocumentUpload.processrequestid,
                      docProgresses
                    )
                  );
                  resolve(true);
                } else {
                  // eslint-disable-next-line prefer-promise-reject-errors
                  reject({
                    status: this.status,
                    statusText: uploadReq.statusText,
                  });
                }
              };

              uploadReq.upload.ontimeout = function onUploadTimeout() {
                console.log(
                  'Document upload timed out. Timout value: ',
                  this.timeout
                );
                cancelledDocUploads = cancelledDocUploads.add(item.documentid);
              };

              uploadReq.upload.onprogress = function onUploadProgress(
                ev: ProgressEvent<EventTarget>
              ) {
                const previousValue = uploadedMap.get(item.documentid);
                const isCancelled = checkCancellation(item.documentid);
                if (isCancelled) {
                  uploadReq.abort();

                  if (!deleteDocument(projectid, item.documentid)) {
                    console.log('Failed to delete document');
                  }
                  cancelledDocUploads.add(item.documentid);
                } else {
                  const diff = ev.loaded - (previousValue || 0);
                  totalUploaded += diff;
                  uploadedMap.set(item.documentid, ev.loaded);
                }

                const docProgresses = generateDocProgressDocList(
                  beginBatchDocumentUpload.items.filter(
                    (item) => !cancelledDocUploads.has(item.documentid)
                  ),
                  item
                );
                progress(
                  new BatchUploadProgress(
                    docProgresses
                      .map((d) => d.totalToUpload)
                      .reduce((a, b) => a + b, 0),
                    docProgresses
                      .map((d) => d.totalUploaded)
                      .reduce((a, b) => a + b, 0),
                    beginBatchDocumentUpload.processrequestid,
                    docProgresses
                  )
                );
                if (isCancelled) {
                  resolve(true);
                }
              };

              uploadReq.upload.onerror = function onUploadError() {
                reject(
                  new Error(
                    JSON.stringify({
                      status: this.status,
                      statusText: uploadReq.statusText,
                    })
                  )
                );
              };

              uploadReq.send(doc);
            });
          };
          acc.push(req);
        }

        return acc;
      },
      [] as { (): Promise<boolean> }[]
    );
    const emptyState = beginBatchDocumentUpload.items.map((doc) => {
      return {
        title: doc.filename,
        id: doc.documentid,
        totalToUpload: doc.filesize,
        totalUploaded: 0,
      } as DocumentUploadProgress;
    });
    progress(
      new BatchUploadProgress(
        totalUploaded,
        documents.map((d) => d.size).reduce((a, b) => a + b, 0),
        beginBatchDocumentUpload.processrequestid,
        emptyState
      )
    );
    const uploadResults = await Promise.all(fileUploads.map((r) => r()));

    if (uploadResults.some((r) => r === false)) {
      throw Error('unable to upload all files');
    }

    const fileCompletions = beginBatchDocumentUpload.items
      .filter((i) => !cancelledDocUploads.has(i.documentid))
      .map(
        (item) => async (): Promise<boolean> => {
          const { completeDocumentUpload } = await new GQLRequest()
            .query(
              `
          mutation CompleteDocUpload($documentid: String!, $projectid: String!) {
            completeDocumentUpload(documentid: $documentid, projectid: $projectid)
          }
          `
            )
            .variables({ documentid: item.documentid, projectid })
            .json<{ completeDocumentUpload: boolean }>();

          return completeDocumentUpload;
        },
        [] as { (): Promise<boolean> }[]
      );

    const completionResults = await Promise.all(
      fileCompletions.map((r) => r())
    );

    if (completionResults.some((r) => r === false)) {
      throw Error('unable to complete all file uploads');
    }

    // NOTE: Toast messages should NOT live in service functions, but putting this one in here
    //       Due to time/complexity contraints. This should be refactored out!
    if (completionResults.length) {
      toast.success(
        toastText.drawingsUploaded.message,
        toastText.drawingsUploaded.title
      );
    }
  } catch (e) {
    console.log('uploadDocuments err:', e);
  }
};

export const getDocuments = async (projectid: ProjectId): Promise<DocList> => {
  const { documents } = await new GQLRequest()
    .query(
      `
      query Docs($projectid: String!) {
        documents(projectid: $projectid) {
          documentid
          projectid
          title
          filename
          filesize
          uploader
          uploadtimestamp
          processrequestid
        }
      }
      `
    )
    .variables({ projectid })
    .json<{ documents: RawDoc[] }>();

  return Immutable.List(documents.map(Doc.deserialize));
};

export const getDocumentUrl = async (
  projectId: string,
  documentId: string
): Promise<string> => {
  const { getDocumentUriPrototype } = await new GQLRequest()
    .query(
      `
      query DocUrl($projectId: String!, $documentId: String!) {
        getDocumentUriPrototype(projectId: $projectId, documentId: $documentId)
      }
      `
    )
    .variables({ projectId, documentId })
    .json<{ getDocumentUriPrototype: string }>();

  return getDocumentUriPrototype;
};

export const getDocumentsByProcessingRequest = async (
  processingRequestid: string
): Promise<DocList> => {
  const { documentsByProcessingRequest } = await new GQLRequest()
    .query(
      `
      query Docs($processingRequestid: String!) {
        documentsByProcessingRequest(processingRequestid: $processingRequestid) {
          documentid
          projectid
          title
          filename
          filesize
          uploader
          uploadtimestamp
          processrequestid
          error
          errorTimestamp
        }
      }
      `
    )
    .variables({ processingRequestid })
    .json<{ documentsByProcessingRequest: RawDoc[] }>();
  return Immutable.List(documentsByProcessingRequest.map(Doc.deserialize));
};

export const getDocument = async (
  projectid: string,
  documentid: string
): Promise<Doc> => {
  const resp = await new GQLRequest()
    .query(
      `
      query Document($projectid: String!, $documentid: String!) {
        document(projectid: $projectid, documentid: $documentid) {
          documentid
          projectid
          title
          filename
          filesize
          uploader
          uploadtimestamp
          processrequestid
        }
      }
      `
    )
    .variables({ projectid, documentid })
    .json<{ document: RawDoc }>();

  return Doc.deserialize(resp.document);
};

export const deleteDocument = async (
  projectid: string,
  documentid: string
): Promise<void> => {
  try {
    await new GQLRequest()
      .query(
        `
          mutation DeleteDocument($projectid: String!, $documentid: String!) {
            deleteDocument(projectid: $projectid, issueid: $documentid)
          }
          `
      )
      .variables({
        projectid,
        documentid,
      })
      .json<{
        deleteDocument: boolean;
      }>();
  } catch (e) {
    console.log('deleteDocument err:', e);
  }
};

export const deleteDocumentsFailedToProcess = async (
  projectid: string,
  documentids: string[]
): Promise<void> => {
  try {
    const result = await new GQLRequest()
      .query(
        `
        mutation($projectid: String!, $documentids: [String!]!) {
          deleteDocumentsFailedToProcess(projectid: $projectid, documentids: $documentids)
        }
        `
      )
      .variables({
        projectid,
        documentids,
      })
      .json<{
        deleteDocumentsFailedToProcess: string[];
      }>();
    console.log(result);
  } catch (e) {
    console.log('deleteDocumentsFailedToProcess err:', e);
  }
};
